import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { useDropzone, FileWithPath } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { Button, Icon } from 'semantic-ui-react'
import { CSSProperties, ThemeContext } from 'styled-components'
import { useAuth } from '~/routes/components/AuthProvider'
import notify from '../Notify'
import { StyledDropzone } from './style'
interface FileUploadPropTypes {
  mimeTypes?: any,
  accept?: Record<string, string[]>
  disabled?: boolean,
  children?: ReactElement,
  multiple?: boolean,
  description?: string,
  onChange?: (acceptedFiles: FileWithPath[], fileName?: string) => void,
  file?: File,
  encodeFiles?: boolean,
  button?: boolean,
  maxWidthDropZone?: CSSProperties['width']
  value?: any
  last?: boolean
  list?: boolean
  limit?: boolean
  sizeLimitInMB?: number
}
const ONE_MILLION = 1000000;

const FileUpload:React.FC<FileUploadPropTypes> = (props) => {
  const { t } = useTranslation()
  const [myFiles, setMyFiles] = useState([])
  const { mimeTypes, children, onChange, multiple, description, encodeFiles, button, list } = props
  const { firstColor } = useContext(ThemeContext)
  const { systemData } = useAuth()
  const languageCode = systemData?.languages?.find(language => language.id === systemData?.logged?.languageId)

  const onDrop = useCallback(acceptedFiles => {
    setMyFiles([...myFiles, ...acceptedFiles])
  }, [myFiles])

  const {
    getRootProps,
    getInputProps,
    open,
    isDragAccept,
    isFocused,
    isDragReject
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: encodeFiles ? handleUploadFiles : onDrop,
    multiple: multiple,
    accept: props.accept
  })

  function handleUploadFiles(acceptedFiles: FileWithPath[]) {
    const file = acceptedFiles[0]
    const reader = new FileReader()
    reader.readAsDataURL(file)
    setMyFiles([...myFiles, ...acceptedFiles])
    return reader.onload = () => {
      const separatorSize = file?.name?.split('.')
      const basicType = file?.name?.split('.')[separatorSize.length - 1]
      if (props.sizeLimitInMB && (file.size / ONE_MILLION) > props.sizeLimitInMB) {
        notify(t('File size is too large'), 'error')
        return
      }
      if (mimeTypes && !mimeTypes.find(mimeType => mimeType === basicType)) {
        notify(t('File type not supported'), 'error')
        return
      }
      //@ts-ignore
      onChange(reader.result, file.name)
    }
  }

  useEffect(() => {
    if (!encodeFiles) {
      const file = myFiles[myFiles.length - 1]
      const separatorSize = file?.name?.split('.')
      const basicType = file?.name?.split('.')[separatorSize.length - 1]
      if (file && mimeTypes && !mimeTypes.find(mimeType => mimeType === basicType)) {
        notify(t('File type not supported'), 'error')
        return
      }
      onChange(file)
    }
  }, [myFiles])

  function renderDropdownSection() {
    return <section className={'dropbox'}>
      {children ?? <StyledDropzone
        {...getRootProps({ isDragAccept, isFocused, isDragReject })}
        onClick={open}
        center={languageCode?.code === 'de' ? 'top' : 'center'}
        $maxWidthDropZone={props.maxWidthDropZone}
        className={'empty'}>
        <>
          <input {...getInputProps()} />
          <div className='uploadContainer'>
            <Icon size='big' className={'icon-upload font-size-4-m'} />
            <p>{description || t('Drop or select a file to upload.')}</p>
          </div>
        </>
      </StyledDropzone>
      }
    </section>
  }

  function renderButtonSection() {
    return <div className=''>
      <div {...getRootProps({ className: '' })}>
        <input {...getInputProps()} />
        {button && <Button basic style={{ background: firstColor, color: 'white' }} type='button' onClick={open}>
          {t('Browse')}
        </Button>
        }
      </div>
    </div>
  }

  function renderListSection() {
    return <div className=''>
      <div {...getRootProps({ className: '' })}>
        <input {...getInputProps()} />
        {props.children}
        {props.last && !props.limit && <Button
          type='button'
          onClick={open}
          primary
          size='mini'
          style={{ background: firstColor, color: 'white' }}
          labelPosition='right'
          content={t('Add new document')}
          icon='file alternate outline'
        />
        }
      </div>
    </div>
  }

  if (button) {
    return renderButtonSection()
  }

  if (list) {
    return renderListSection()
  }

  return renderDropdownSection()
}

export default FileUpload
